import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Eye, RotateCcw } from 'lucide-react';
import { SkeletonDashboard } from '@/components/loading/Skeleton';
import { qryCopilotHistoryInsightsApi } from '@/api/copilot';
import { EXTERNAL_USER_ID } from '@/config/env';
interface HistoryInsight {
  id: string;
  date: string;
  time: string;
  tags: string[];
  triggeredDialogue: string;
  insightContent: string;
  status: '已采纳' | '执行中' | '已完成' | '已分享';
}

interface HistoryInsightsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const HistoryInsightsDialog: React.FC<HistoryInsightsDialogProps> = ({ open, onOpenChange }) => {
  // 模拟历史洞察数据
  const [insights] = useState<HistoryInsight[]>([
    {
      id: '1',
      date: '2024-01-20',
      time: '14:30',
      tags: ['产品推荐', '市场解读'],
      triggeredDialogue: '我对新能源和消费板块比较感兴趣,有没有相关的基金可以推荐?',
      insightContent: '基于对新能源和消费板块的兴趣,推荐新能源ETF、消费精选混合基金,并分析板块趋势',
      status: '已采纳'
    },
    {
      id: '2',
      date: '2024-01-20',
      time: '10:25',
      tags: ['资产配置'],
      triggeredDialogue: '想调整一下我的投资组合,有什么好的建议吗?',
      insightContent: '分析当前投资组合,建议调整资产配置比例优化收益',
      status: '执行中'
    },
    {
      id: '3',
      date: '2024-01-19',
      time: '16:45',
      tags: ['产品解读', '产品推荐'],
      triggeredDialogue: '还想了解一下这些板块的ETF产品',
      insightContent: 'ETF产品深度解读及相关产品配置建议',
      status: '已完成'
    },
    {
      id: '4',
      date: '2024-01-18',
      time: '11:20',
      tags: ['市场解读'],
      triggeredDialogue: '最近市场波动挺大的',
      insightContent: '市场波动分析及投资策略建议',
      status: '已分享'
    },
    {
      id: '5',
      date: '2024-01-17',
      time: '15:10',
      tags: ['产品推荐', '资产配置'],
      triggeredDialogue: '我想了解一下债券基金的投资机会',
      insightContent: '债券市场分析及优质债券基金推荐',
      status: '已完成'
    },
    {
      id: '6',
      date: '2024-01-16',
      time: '09:30',
      tags: ['市场解读', '产品解读'],
      triggeredDialogue: '科技股最近表现怎么样？',
      insightContent: '科技股市场分析及投资机会解读',
      status: '已采纳'
    }
  ]);

  const [loading, setLoading] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case '已采纳':
        return 'text-blue-600 bg-blue-50';
      case '执行中':
        return 'text-orange-600 bg-orange-50';
      case '已完成':
        return 'text-green-600 bg-green-50';
      case '已分享':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getTagColor = (tag: string) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-purple-100 text-purple-700',
      'bg-orange-100 text-orange-700',
      'bg-pink-100 text-pink-700'
    ];
    const index = tag.length % colors.length;
    return colors[index];
  };

  useEffect(() => {
    setLoading(open);
    if (open) {
      qryCopilotHistoryInsightsApi({
        externalUserId: EXTERNAL_USER_ID,
        followUserId: 'QiMaHeJiuDaTianXiaabel',
        companyId: '60011'
      })
        .then(res => {
          console.log(res);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-hidden bg-white">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="text-xl font-semibold text-gray-900">历史洞察</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex h-full w-full items-center justify-center">
            <SkeletonDashboard className="min-h-[80vh] w-full px-0" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* 头部操作栏 */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">徐客户历史洞察记录</h2>
              <Button className="flex items-center gap-2 bg-[#5959EB] text-white hover:bg-[#5959EB]/90">
                <Download className="h-4 w-4" />
                导出
              </Button>
            </div>

            {/* 历史洞察列表 */}
            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-2">
              {insights.map(insight => (
                <div
                  key={insight.id}
                  className="rounded-lg border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4"
                >
                  {/* 日期时间和标签 */}
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700">
                        {insight.date} {insight.time}
                      </span>
                      <div className="flex gap-2">
                        {insight.tags.map((tag, index) => (
                          <span
                            key={index}
                            className={`rounded-full px-2 py-1 text-xs font-medium ${getTagColor(tag)}`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(insight.status)}`}
                    >
                      {insight.status}
                    </span>
                  </div>

                  {/* 触发对话 */}
                  <div className="mb-3">
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-100">
                        <div className="h-2 w-2 rounded-full bg-blue-600"></div>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700">触发对话:</span>
                        <p className="mt-1 text-sm text-gray-600">"{insight.triggeredDialogue}"</p>
                      </div>
                    </div>
                  </div>

                  {/* 洞察内容 */}
                  <div className="mb-3">
                    <span className="text-sm font-medium text-gray-700">洞察内容:</span>
                    <p className="mt-1 text-sm text-gray-600">{insight.insightContent}</p>
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex items-center justify-between">
                    <div className="flex gap-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex h-auto items-center gap-1 p-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <Eye className="h-4 w-4" />
                        查看详情
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex h-auto items-center gap-1 p-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      >
                        <RotateCcw className="h-4 w-4" />
                        重新应用
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HistoryInsightsDialog;
